// src/pages/Insurance.jsx
import React, { useEffect, useState, useRef } from "react";
import { supabase } from "../lib/supabaseClient";
import { Link } from "react-router-dom";
import QRCodeLib from "qrcode";
import { useTranslation } from "react-i18next";
import { AlertTriangle, CheckCircle2 } from "lucide-react";

// ─── QR Code ─────────────────────────────────────────────────────────────────

function QRCodeCanvas({ value, size = 80, level = "H", includeMargin = true }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    if (canvasRef.current && value) {
      QRCodeLib.toCanvas(canvasRef.current, value, {
        width: size, margin: includeMargin ? 4 : 0,
        errorCorrectionLevel: level.toLowerCase(),
        color: { dark: "#000000", light: "#FFFFFF" },
      });
    }
  }, [value, size, level, includeMargin]);
  return <canvas ref={canvasRef} style={{ maxWidth: "100%", height: "auto" }} />;
}

// ─── Widget alertes sous-assurance ───────────────────────────────────────────

function UnderinsuranceAlerts({ insurances, artworks, t, locale }) {
  // Construit la map artwork_id → données artwork (valeur estimée)
  const artworkMap = {};
  artworks.forEach((a) => { artworkMap[a.id] = a; });

  const rows = insurances
    .map((ins) => {
      const artwork = artworkMap[ins.artwork_id];
      if (!artwork) return null;
      const estimated = artwork.current_estimated_value || artwork.valuation_market_value || null;
      const insured = ins.value || 0;
      if (!estimated) return null;
      const gap = estimated - insured;
      const ratio = insured / estimated; // 1.0 = parfaitement couvert
      return { ins, artwork, estimated, insured, gap, ratio };
    })
    .filter(Boolean)
    .sort((a, b) => a.ratio - b.ratio); // les moins bien couverts en premier

  if (rows.length === 0) return null;

  const alerts    = rows.filter((r) => r.ratio < 0.9);
  const ok        = rows.filter((r) => r.ratio >= 0.9);
  const totalEst  = rows.reduce((s, r) => s + r.estimated, 0);
  const totalIns  = rows.reduce((s, r) => s + r.insured, 0);
  const totalGap  = totalEst - totalIns;

  const fmt = (n) =>
    new Intl.NumberFormat(locale, { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

  return (
    <div style={{ marginBottom: 40 }}>
      <h2 style={{ fontFamily: "Georgia, serif", fontSize: 20, color: "#1a1a1a", marginBottom: 16 }}>
        {t("insurance.coverage.title")}
      </h2>

      {/* Récap global */}
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 20,
      }}>
        {[
          { label: t("insurance.coverage.totalEstimated"), value: fmt(totalEst), color: "#1a1a1a" },
          { label: t("insurance.coverage.totalInsured"),  value: fmt(totalIns), color: "#1a1a1a" },
          { label: t("insurance.coverage.gap"),    value: fmt(totalGap), color: totalGap > 0 ? "#dc2626" : "#16a34a" },
        ].map((item) => (
          <div key={item.label} style={{
            padding: "14px 16px", background: "#fafaf8",
            border: "1px solid #f0ece5", borderRadius: 6,
          }}>
            <div style={{ fontSize: 10, fontFamily: "system-ui", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>
              {item.label}
            </div>
            <div style={{ fontSize: 18, fontFamily: "Georgia, serif", fontWeight: 700, color: item.color }}>
              {item.value}
            </div>
          </div>
        ))}
      </div>

      {/* Alertes */}
      {alerts.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontFamily: "system-ui", fontWeight: 700, color: "#dc2626", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
            ⚠ {t(alerts.length > 1 ? 'insurance.coverage.underinsured_plural' : 'insurance.coverage.underinsured', { count: alerts.length })}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {alerts.map(({ ins, artwork, estimated, insured, gap, ratio }) => (
              <div key={ins.id} style={{
                display: "flex", alignItems: "center", gap: 14,
                padding: "12px 16px", background: "#fef2f2",
                border: "1px solid #fecaca", borderRadius: 6,
              }}>
                <AlertTriangle size={16} color="#dc2626" style={{ flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: "Georgia, serif", fontSize: 14, color: "#1a1a1a", marginBottom: 2 }}>
                    {artwork.title}
                    {artwork.artist && <span style={{ color: "#888", fontWeight: 400 }}> — {artwork.artist}</span>}
                  </div>
                  {/* Barre de progression */}
                  <div style={{ height: 4, background: "#fecaca", borderRadius: 2, marginBottom: 4, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${Math.min(ratio * 100, 100)}%`, background: ratio < 0.5 ? "#dc2626" : "#f97316", borderRadius: 2 }} />
                  </div>
                  <div style={{ fontSize: 11, fontFamily: "system-ui", color: "#888" }}>
                    {t("insurance.coverage.insuredLabel")} : <strong style={{ color: "#1a1a1a" }}>{fmt(insured)}</strong>
                    {" · "}{t("insurance.coverage.estimatedLabel")} : <strong style={{ color: "#1a1a1a" }}>{fmt(estimated)}</strong>
                    {" · "}{t("insurance.coverage.gapLabel")} : <strong style={{ color: "#dc2626" }}>{fmt(gap)}</strong>
                    {" · "}<span style={{ color: ratio < 0.5 ? "#dc2626" : "#d97706", fontWeight: 700 }}>
                      {t("insurance.coverage.percentCovered", { percent: Math.round(ratio * 100) })}
                    </span>
                  </div>
                </div>
                <Link
                  to={`/insurance/${ins.artwork_id}`}
                  style={{ fontSize: 11, color: "#c9a961", fontFamily: "system-ui", fontWeight: 600, textDecoration: "none", whiteSpace: "nowrap" }}
                >
                  {t("insurance.coverage.update")}
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Œuvres bien couvertes */}
      {ok.length > 0 && (
        <div>
          <div style={{ fontSize: 11, fontFamily: "system-ui", fontWeight: 700, color: "#16a34a", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
            ✓ {t(ok.length > 1 ? 'insurance.coverage.wellCovered_plural' : 'insurance.coverage.wellCovered', { count: ok.length })}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {ok.map(({ ins, artwork, estimated, insured, ratio }) => (
              <div key={ins.id} style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "10px 14px", background: "#f0fdf4",
                border: "1px solid #bbf7d0", borderRadius: 5,
              }}>
                <CheckCircle2 size={14} color="#16a34a" style={{ flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: "Georgia, serif", fontSize: 13, color: "#1a1a1a" }}>
                    {artwork.title}
                    {artwork.artist && <span style={{ color: "#888" }}> — {artwork.artist}</span>}
                  </div>
                  <div style={{ fontSize: 11, fontFamily: "system-ui", color: "#16a34a", marginTop: 2 }}>
                    {fmt(insured)} {t("insurance.coverage.insuredLabel").toLowerCase()} · {fmt(estimated)} {t("insurance.coverage.estimatedLabel").toLowerCase()} · {t("insurance.coverage.covered", { pct: Math.round(ratio * 100) })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────

const Insurance = () => {
  const { t, i18n } = useTranslation();
  const language = String(i18n.resolvedLanguage || i18n.language || "fr").toLowerCase();
  const locale = language.startsWith("de") ? "de-DE" : language.startsWith("en") ? "en-US" : "fr-FR";
  const [isLoading, setIsLoading] = useState(true);
  const [insurances, setInsurances] = useState([]);
  const [artworks, setArtworks] = useState([]);
  const [error, setError] = useState(null);
  const [insurers, setInsurers] = useState([]);
  const [formData, setFormData] = useState({
    artwork_id: "", value: "", insurer: "",
    insurance_type: "", idnumber_insurance: "", expiry_date: "",
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        if (!sessionData?.session?.user) {
          setError(t("insurance.authRequired"));
          setIsLoading(false);
          return;
        }
        const userId = sessionData.session.user.id;

        // On récupère current_estimated_value et valuation_market_value pour les alertes
        const { data: artworksData } = await supabase
          .from("artworks")
          .select("id, title, artist, year, value, current_estimated_value, valuation_market_value")
          .eq("client_id", userId);

        const { data: insuranceData } = await supabase
          .from("insurance")
          .select("*")
          .eq("client_id", userId);

        const { data: insurerData } = await supabase
          .from("preferred_insurers")
          .select("name")
          .eq("client_id", userId);

        setArtworks(artworksData || []);
        setInsurances(insuranceData || []);
        setInsurers((insurerData || []).map((i) => i.name));
      } catch (err) {
        console.error("Erreur chargement assurances:", err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [t]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error(t("insurance.notConnected"));
      const { data, error } = await supabase
        .from("insurance")
        .insert([{
          client_id: session.user.id,
          artwork_id: formData.artwork_id,
          value: Number(formData.value),
          insurer: formData.insurer,
          insurance_type: formData.insurance_type,
          expiry_date: formData.expiry_date,
          idnumber_insurance: formData.idnumber_insurance,
        }])
        .select()
        .single();
      if (error) throw error;
      setInsurances((prev) => [...prev, data]);
      setFormData({ artwork_id: "", value: "", insurer: "", insurance_type: "", idnumber_insurance: "", expiry_date: "" });
      alert(t("insurance.successAdded", "Assurance ajoutée avec succès."));
    } catch (err) {
      console.error("Erreur ajout assurance:", err);
      alert(err.message);
    }
  };

  if (isLoading) return <div className="flex justify-center items-center h-64">{t("insurance.loading")}</div>;
  if (error) return <div className="text-center text-red-600">{t("insurance.error")} : {error}</div>;

  // Artworks qui ont une valeur estimée (pour la section alertes)
  const artworksWithEstimate = artworks.filter(
    (a) => a.current_estimated_value || a.valuation_market_value
  );

  return (
    <div className="max-w-5xl mx-auto px-6 py-12">
      <div className="flex items-center mb-10">
        <Link to="/dashboard" className="text-gray-dark hover:text-black mr-4">
          {t("insurance.back")}
        </Link>
        <h1 className="font-serif text-3xl">{t("insurance.title")}</h1>
      </div>

      {/* Cette page sert à enregistrer les couvertures déjà existantes.
          Les demandes de nouvelle assurance restent dans Actions → Assurance. */}
      <div style={{
        marginBottom: 32,
        padding: "18px 20px",
        background: "#fafaf8",
        border: "1px solid #e0d8cc",
        borderRadius: 8,
      }}>
        <div style={{ fontFamily: "Georgia, serif", fontSize: 17, color: "#1a1a1a", marginBottom: 6 }}>
          {t("insurance.recordIntro.title")}
        </div>
        <div style={{ fontSize: 13, color: "#666", lineHeight: 1.6 }}>
          {t("insurance.recordIntro.body")}
        </div>
        <div style={{ fontSize: 12, color: "#888", lineHeight: 1.6, marginTop: 8 }}>
          {t("insurance.recordIntro.newInsurance")}
        </div>
      </div>

      {/* ── Section alertes sous-assurance ── */}
      {insurances.length > 0 && artworksWithEstimate.length > 0 && (
        <UnderinsuranceAlerts insurances={insurances} artworks={artworks} t={t} locale={locale} />
      )}

      {/* ── Liste des polices ── */}
      <div className="mb-12">
        <h2 style={{ fontFamily: "Georgia, serif", fontSize: 20, marginBottom: 16, color: "#1a1a1a" }}>
          {t("insurance.insuredWorks")}
        </h2>
        {insurances.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {insurances.map((insurance) => {
              const artwork = artworks.find((a) => a.id === insurance.artwork_id);
              // Vérif expiration
              const isExpired = insurance.expiry_date && new Date(insurance.expiry_date) < new Date();
              const expireSoon = insurance.expiry_date && !isExpired &&
                (new Date(insurance.expiry_date) - new Date()) < 60 * 24 * 3600 * 1000; // < 60 jours

              return (
                <div key={insurance.id} style={{
                  border: isExpired ? "1px solid #fecaca" : expireSoon ? "1px solid #fde68a" : "1px solid #e0d8cc",
                  borderRadius: 8, padding: "16px 18px", background: "#fff",
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                    <h3 style={{ fontFamily: "Georgia, serif", fontSize: 15, color: "#1a1a1a", margin: 0 }}>
                      {artwork?.title || t("insurance.unknownArtwork", "Œuvre inconnue")}
                    </h3>
                    {isExpired && (
                      <span style={{ fontSize: 10, fontWeight: 700, color: "#dc2626", background: "#fef2f2", padding: "2px 7px", borderRadius: 3 }}>
                        {t("insurance.coverage.expired")}
                      </span>
                    )}
                    {expireSoon && !isExpired && (
                      <span style={{ fontSize: 10, fontWeight: 700, color: "#d97706", background: "#fffbeb", padding: "2px 7px", borderRadius: 3 }}>
                        {t("insurance.coverage.expireSoon")}
                      </span>
                    )}
                  </div>
                  {artwork?.artist && (
                    <p style={{ fontSize: 12, color: "#888", fontFamily: "system-ui", marginBottom: 8 }}>{artwork.artist}</p>
                  )}
                  <div style={{ fontSize: 13, fontFamily: "system-ui", color: "#555", lineHeight: 1.8 }}>
                    <div><span style={{ color: "#aaa" }}>{t("insurance.value")} :</span> <strong>{insurance.value?.toLocaleString(locale)} €</strong></div>
                    <div><span style={{ color: "#aaa" }}>{t("insurance.insurer")} :</span> {insurance.insurer}</div>
                    <div><span style={{ color: "#aaa" }}>{t("insurance.type")} :</span> {insurance.insurance_type}</div>
                    {insurance.idnumber_insurance && (
                      <div><span style={{ color: "#aaa" }}>{t("insurance.policyNumber")} :</span> {insurance.idnumber_insurance}</div>
                    )}
                    <div style={{ color: isExpired ? "#dc2626" : expireSoon ? "#d97706" : "#aaa" }}>
                      <span style={{ color: "#aaa" }}>{t("insurance.validUntil")} :</span>{" "}
                      {insurance.expiry_date ? new Date(insurance.expiry_date).toLocaleDateString(locale) : t("insurance.notSpecified")}
                    </div>
                  </div>
                  <div style={{ display: "flex", justifyContent: "center", marginTop: 12 }}>
                    <QRCodeCanvas value={`${window.location.origin}/insurance/${insurance.id}`} size={70} />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ background: "#fafaf8", border: "1px solid #e0d8cc", borderRadius: 8, padding: 32, textAlign: "center" }}>
            <p style={{ fontFamily: "Georgia, serif", fontSize: 18, color: "#1a1a1a", marginBottom: 8 }}>
              {t("insurance.noneYet.title")}
            </p>
            <p style={{ fontSize: 13, color: "#888", marginBottom: 20 }}>
              {t("insurance.noneYet.text")}
            </p>
          </div>
        )}
      </div>

      {/* ── Formulaire nouvelle assurance ── */}
      <div>
        <h2 style={{ fontFamily: "Georgia, serif", fontSize: 20, marginBottom: 20, color: "#1a1a1a" }}>
          {t("insurance.section.new")}
        </h2>
        {artworks.length === 0 ? (
          <div style={{ textAlign: "center", padding: 32, background: "#fafaf8", border: "1px solid #e0d8cc", borderRadius: 8 }}>
            <p style={{ fontSize: 13, color: "#888", marginBottom: 16 }}>{t("insurance.noArtworks")}</p>
            <Link to="/add" className="btn-artsy btn-primary-artsy">{t("insurance.addArtwork")}</Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <select name="artwork_id" value={formData.artwork_id} onChange={handleChange} required className="form-control-artsy">
                <option value="">{t("insurance.selectArtwork")}</option>
                {artworks.map((a) => (
                  <option key={a.id} value={a.id}>{a.title}{a.artist ? ` — ${a.artist}` : ""}</option>
                ))}
              </select>

              <input
                type="number"
                name="value"
                value={formData.value}
                onChange={handleChange}
                placeholder={t("insurance.form.value")}
                className="form-control-artsy"
                required
              />

              <select name="insurance_type" value={formData.insurance_type} onChange={handleChange} required className="form-control-artsy">
                <option value="">{t("insurance.form.type")}</option>
                <option value="Vol">{t("insurance.types.theft")}</option>
                <option value="Incendie">{t("insurance.types.fire")}</option>
                <option value="Dégâts des eaux">{t("insurance.types.waterDamage")}</option>
                <option value="Transport">{t("insurance.types.transport")}</option>
                <option value="Tous risques">{t("insurance.types.allRisks")}</option>
                <option value="Clou à clou">{t("insurance.types.nailToNail")}</option>
              </select>

              <div>
                <select name="insurer" value={formData.insurer} onChange={handleChange} required className="form-control-artsy">
                  <option value="">{t("insurance.form.insurer")}</option>
                  {insurers.map((insurer, i) => (
                    <option key={i} value={insurer}>{insurer}</option>
                  ))}
                </select>
                <div style={{ textAlign: "right", marginTop: 6 }}>
                  <Link to="/add-insurer" style={{ fontSize: 12, color: "#c9a961", textDecoration: "underline" }}>
                    {t("insurance.addInsurer")}
                  </Link>
                </div>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <input
                type="date"
                name="expiry_date"
                value={formData.expiry_date}
                onChange={handleChange}
                className="form-control-artsy"
                required
              />
              <input
                type="text"
                name="idnumber_insurance"
                value={formData.idnumber_insurance}
                onChange={handleChange}
                placeholder={t("insurance.form.policyPlaceholder")}
                className="form-control-artsy"
              />
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button type="submit" className="btn-artsy btn-primary-artsy">
                {t("insurance.submit")}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default Insurance;